import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import {
  ArrowLeft,
  CalendarDays,
  Clock,
  User,
  Wallet,
  BadgeCheck,
  Timer,
} from "lucide-react";
import MainHeading from "../../components/common/MainHeading";
import { Button } from "../../components/ui/button";
import { ThrowError } from "../../api/callApi";
import { RoutesLink } from "../../utility/RouteLinks";
import type { IAttendanceRecord } from "@/interface/attendance.model";
import { AttendanceService } from "@/services/Attendance/Attendance";
import {
  LABOR_STATUS_OPTIONS,
  LABOR_STATUS_STYLE_MAP,
} from "./LaborAttendanceLegend";
import type { LaborAttendanceStatus } from "../../interface/laborAttendance.interface";

// API status ("present" / "absent" / "halfday" / "pending") -> UI status
// used by LABOR_STATUS_STYLE_MAP / LABOR_STATUS_OPTIONS elsewhere in this feature.
const toUiStatus = (status: IAttendanceRecord["status"]): LaborAttendanceStatus => {
  switch (status) {
    case "present":
      return "PRESENT";
    case "absent":
      return "ABSENT";
    case "halfday":
      return "HALF_DAY";
    default:
      return "--";
  }
};

// Reusable DetailField, same pattern as LaborDetail.
const DetailField = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 rounded-lg bg-primary/8 text-primary flex items-center justify-center shrink-0 mt-0.5">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-900 truncate">
        {value ?? "—"}
      </p>
    </div>
  </div>
);

const AttendanceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [record, setRecord] = useState<IAttendanceRecord | null>(null);

  useEffect(() => {
    const fetchAttendanceDetail = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const response = await AttendanceService.getAttendanceDetail(id);
        if (!response?.success || !response.data) {
          throw new Error("Failed to load attendance record");
        }
        setRecord(response.data);
      } catch (error) {
        ThrowError(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAttendanceDetail();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">
        Loading attendance details...
      </div>
    );
  }

  if (!record) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-sm text-muted-foreground">
          Attendance record not found.
        </p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>
    );
  }

  const uiStatus = toUiStatus(record.status);
  const statusLabel =
    LABOR_STATUS_OPTIONS.find((o) => o.value === uiStatus)?.label ?? uiStatus;

  // Worked hours derived from check-in/check-out, handling overnight shifts.
  const hoursWorked = (() => {
    if (!record.checkInTime || !record.checkOutTime) return null;
    const start = moment(record.checkInTime, "HH:mm");
    let end = moment(record.checkOutTime, "HH:mm");
    if (end.isBefore(start)) end = end.add(1, "day");
    return Math.round((end.diff(start, "minutes") / 60) * 100) / 100;
  })();

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          className="h-9 w-9 p-0 hover:bg-muted shrink-0"
          onClick={() => navigate(-1)}
          type="button"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <MainHeading title="Attendance Details" />
          <p className="text-[13px] text-gray-400 -mt-0.5">
            Punch record for {moment(record.date).format("DD MMM YYYY")}
          </p>
        </div>
      </div>

      {/* Main Card */}
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
        {/* Summary header */}
        <div className="flex items-center gap-4 px-6 py-5 sm:px-8 border-b border-gray-100">
          <div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-lg shrink-0">
            {record.workerId.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold text-gray-900 truncate">
              {record.workerId}
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
              <span className="text-xs text-gray-400">
                {moment(record.date).format("DD-MM-YYYY")}
              </span>
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium border rounded-full ${LABOR_STATUS_STYLE_MAP[uiStatus]}`}
              >
                {statusLabel}
              </span>
            </div>
          </div>
        </div>

        {/* Details */}
        <section className="px-6 py-7 sm:px-8">
          <div className="flex items-start gap-3 mb-6">
            <div className="w-9 h-9 rounded-lg bg-primary/8 text-primary flex items-center justify-center shrink-0">
              <Clock size={17} />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-gray-900">
                Punch Details
              </h3>
              <p className="text-[13px] text-gray-400 mt-0.5">
                Check-in, check-out, and worked hours
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <DetailField
              icon={<User size={15} />}
              label="Worker ID"
              value={record.workerId}
            />
            <DetailField
              icon={<CalendarDays size={15} />}
              label="Date"
              value={moment(record.date).format("DD-MM-YYYY")}
            />
            <DetailField
              icon={<Clock size={15} />}
              label="Check-in Time"
              value={
                record.checkInTime
                  ? moment(record.checkInTime, "HH:mm").format("h:mm A")
                  : undefined
              }
            />
            <DetailField
              icon={<Clock size={15} />}
              label="Check-out Time"
              value={
                record.checkOutTime
                  ? moment(record.checkOutTime, "HH:mm").format("h:mm A")
                  : undefined
              }
            />
            <DetailField
              icon={<Timer size={15} />}
              label="Hours Worked"
              value={hoursWorked ? `${hoursWorked} hrs` : undefined}
            />
            <DetailField
              icon={<Wallet size={15} />}
              label="Paid For This Day"
              value={record.paidForThisDays ? "Yes" : "No"}
            />
            <DetailField
              icon={<BadgeCheck size={15} />}
              label="Status"
              value={statusLabel}
            />
            {record.createdAt && (
              <DetailField
                icon={<CalendarDays size={15} />}
                label="Recorded On"
                value={moment(record.createdAt).format("DD-MM-YYYY, h:mm A")}
              />
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AttendanceDetail;